import { HttpRequest } from '@angular/common/http';

/**
 * HTTP Transfer Cache Configuration
 * 
 * This configuration provides comprehensive settings for Angular's HTTP Transfer Cache
 * which prevents duplicate HTTP requests during SSR to client hydration transition.
 */

/**
 * HTTP Transfer Cache Options interface
 * Based on Angular's internal HttpTransferCacheOptions
 */
export interface HttpTransferCacheOptions {
    includeHeaders?: string[];
    includeRequestsWithAuthHeaders?: boolean;
    includePostRequests?: boolean;
    filter?: (req: HttpRequest<any>) => boolean;
}

/**
 * Headers that should be included in the cache key for proper cache isolation
 * These headers affect the response and should be part of the cache key
 */
export const CACHE_HEADERS = [
    'authorization',      // User authentication
    'clientid',          // API client identification
    'clientsecret',      // API client secret
    'saleschannel',      // Sales channel context
    'content-type',      // Request content type
    'accept',            // Response format preference
    'accept-language',   // Localization
    'accept-encoding',   // Compression preferences
    'user-agent',        // Client identification
    'x-requested-with',  // AJAX identification
] as const;

/**
 * URL patterns that should NOT be cached (real-time data)
 */
export const EXCLUDE_CACHE_PATTERNS = [
    '/realtime/',
    '/notifications/',
    '/live-updates/',
    '/websocket/',
    '/sse/',
    '/events/',
    '/streaming/',
    '/live/',
    '/push/',
    '/poll/',
    '/heartbeat/',
    '/ping/',
    '/health-check/',
    '/status/',
    '/metrics/',
    '/analytics/track',
    '/logging/',
    '/audit/',
] as const;

/**
 * URL patterns for POST requests that SHOULD be cached
 * (typically search, filter, and list operations)
 */
export const INCLUDE_POST_PATTERNS = [
    '/search',
    '/filter',
    '/products/list',
    '/categories/list',
    '/brands/list',
    '/collections/list',
    '/users/search',
    '/orders/search',
    '/inventory/search',
    '/reports/generate',
    '/export/data',
    '/query',
    '/lookup',
    '/find',
] as const;

/**
 * Custom filter function to determine which HTTP requests should be cached
 * 
 * @param req - The HTTP request to evaluate
 * @returns true if the request should be cached, false otherwise
 */
export function httpTransferCacheFilter(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();
    const method = req.method.toUpperCase();

    // Always exclude certain patterns regardless of method
    if (EXCLUDE_CACHE_PATTERNS.some(pattern => url.includes(pattern))) {
        return false;
    }

    // Handle GET requests
    if (method === 'GET') {
        // Cache most GET requests by default
        return true;
    }

    // Handle POST requests - only cache specific patterns
    if (method === 'POST') {
        return INCLUDE_POST_PATTERNS.some(pattern => url.includes(pattern));
    }

    // Don't cache PUT, DELETE, PATCH, OPTIONS, HEAD requests
    return false;
}

/**
 * Environment-specific cache configuration
 */
export function getHttpTransferCacheConfig(environment: 'development' | 'production' = 'production'): HttpTransferCacheOptions {
    const baseConfig: HttpTransferCacheOptions = {
        includeHeaders: [...CACHE_HEADERS],
        includeRequestsWithAuthHeaders: true,
        includePostRequests: true,
        filter: httpTransferCacheFilter,
    };

    // Development-specific adjustments
    if (environment === 'development') {
        return {
            ...baseConfig,
            // In development, you might want to be more restrictive or add debugging
            filter: (req) => {
                const shouldCache = httpTransferCacheFilter(req);


                return shouldCache;
            },
        };
    }

    return baseConfig;
}

/**
 * Utility function to check if a request would be cached
 * Useful for debugging and testing
 */
export function wouldRequestBeCached(method: string, url: string): boolean {
    const mockRequest = { method, url } as HttpRequest<any>;
    return httpTransferCacheFilter(mockRequest);
}

/**
 * Cache statistics interface for monitoring
 */
export interface CacheStats {
    totalRequests: number;
    cachedRequests: number;
    cacheHitRate: number;
    excludedPatterns: string[];
    includedPatterns: string[];
}

/**
 * Get cache configuration statistics
 */
export function getCacheStats(): CacheStats {
    return {
        totalRequests: 0, // This would be populated by actual usage
        cachedRequests: 0, // This would be populated by actual usage
        cacheHitRate: 0, // This would be calculated from actual usage
        excludedPatterns: [...EXCLUDE_CACHE_PATTERNS],
        includedPatterns: [...INCLUDE_POST_PATTERNS],
    };
}

/**
 * Default HTTP Transfer Cache configuration for the application
 */
export const DEFAULT_HTTP_TRANSFER_CACHE_CONFIG = getHttpTransferCacheConfig('production');
